import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import React from "react";

export default function ParetoChart({
  totalOrders,
  graphAllLabels,
  graphAllValues,
  setSelectedBar,
}) {
  // Register the required chart components
  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Legend,
    Tooltip
  );

  // Handle bar click event function which will be used to set the selected bar details
  const handleBarClick = (event, elements) => {
    const table = document.getElementById("ReplaceRefundOrders");
    table.scrollIntoView({ behavior: "smooth" });

    if (elements?.length > 0) {
      const clickedElement = elements[1];
      const index = clickedElement?.index;
      const datasetIndex = clickedElement?.datasetIndex;

      // Only respond to clicks on the bar dataset (index 0), not the line dataset
      if (datasetIndex === 0) {
        const label = graphAllLabels[index];
        const value = barValues[index];
        const percentage = percentageValues[index];

        setSelectedBar({
          label,
          value,
          percentage,
          index,
        });
      }
    }
  };

  // Calculate percentage values for the line chart
  const percentageValues =
    graphAllValues
      ?.map((value) => Number(((value / totalOrders) * 100).toFixed(2)))
      ?.filter((value) => value !== 0) || [];

  // Calculate the bar values for the bar chart and filter out 0 values
  const barValues = graphAllValues
    ?.map((value) => Number(value))
    ?.filter((value) => value !== 0);

  // Create the chart data
  const data = {
    labels: graphAllLabels,
    datasets: [
      {
        type: "bar",
        label: "Reasons",
        data: barValues,
        backgroundColor: "#CB998D",
        order: 2,
      },
      {
        type: "line",
        label: "%",
        data: percentageValues,
        yAxisID: "y1",
        borderColor: "#666666",
        borderWidth: 2,
        fill: false,
        pointRadius: 0,
        pointBackgroundColor: "#666666",
        order: 1,
        hidden: false,
      },
    ],
  };

  const options = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    onClick: handleBarClick,
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            const datasetLabel = context.dataset.label || "";
            if (context.datasetIndex === 1) {
              return `${datasetLabel}: ${context.parsed.y.toFixed(2)}%`;
            } else {
              return `${datasetLabel}: ${context.parsed.y}`;
            }
          },
        },
      },
      legend: {
        labels: {
          filter: function (item) {
            return item.text === "Reasons";
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
        },
        grid: {
          color: "#e0e0e0",
        },
      },
      y1: {
        beginAtZero: true,
        position: "right",
        title: {
          display: true,
        },
        grid: {
          drawOnChartArea: false,
        },
        max: Number(Math.max(...percentageValues) + 4).toFixed(2),
        ticks: {
          callback: function (value) {
            return value + "%";
          },
        },
      },
    },
  };

  // Add a custom plugin to draw percentage values directly on the chart
  const labelPlugin = {
    id: "labelPlugin",
    afterDatasetsDraw(chart) {
      const { ctx, data } = chart;
      // Get the line dataset (index 1)
      //Customize the line chart text and other details
      const lineDataset = data.datasets[1];
      const meta = chart.getDatasetMeta(1);
      if (!meta.hidden) {
        ctx.save();
        ctx.font = "bold 10px Arial";
        ctx.fillStyle = "#FFFFFF";
        ctx.textAlign = "center";
        meta.data.forEach((dataPoint, index) => {
          const value = lineDataset.data[index];
          ctx.fillText(`${value.toFixed(2)}%`, dataPoint.x, dataPoint.y - 10);
        });
        ctx.restore();
      }

      //Get the bar dataset (index 0)
      //Customize the bar chart text and other details
      const barDataset = data.datasets[0];
      const barMeta = chart.getDatasetMeta(0);
      if (!barMeta.hidden) {
        ctx.fillStyle = "rgb(122, 122, 122)";
        barMeta.data.forEach((dataPoint, index) => {
          const value = barDataset.data[index];
          ctx.fillText(`${value}`, dataPoint.x, dataPoint.y - 10);
        });
        ctx.restore();
      }
    },
  };

  return (
    <>
      <Bar
        data={data}
        options={options}
        plugins={[labelPlugin]}
        style={{ backgroundColor: "#FAFAFB", padding: "20px" }}
      />
    </>
  );
}
